import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useParams } from 'react-router'
import { useMediaQuery } from 'react-responsive'
import Skeleton from 'react-loading-skeleton'
import { usePortal } from '../hooks/usePortal'
import { useAxios } from '../hooks/useAxios'
import { type ArticleData } from '../server/utils/getArticlesData'

import Card from '../components/Card'
import ContactCard from '../components/ContactCard'
import CommunityDetailCard from '../components/CommunityDetailCard'
import Breadcrumbs from '../components/Breadcrumbs'
import EventDetailCard from '../components/EventDetailCard'
import getArticle from '../api/getArticle'
import TextContent from '../components/TextContent'
import NotFound from '../components/NotFound'
import EventRating from './../components/EventRating'
import Btn from '../components/Btn';
import PageNotification from '../components/PageNotification'
import { EventModerationStatusEnum } from '../enums/EventModerationStatusEnum'

interface Props {
  type: 'event' | 'community'
}

const BREADCRUMBS = {
  community: [
    {
      label: 'Главная',
      path: '/'
    },
    {
      label: 'Сообщества',
      path: '/communities'
    }
  ],
  event: [
    {
      label: 'Главная',
      path: '/'
    },
    {
      label: 'Мероприятия',
      path: '/events'
    }
  ]
}

const Article: React.FC<Props> = ({ type }) => {
  const [breadcrumbs, setBreadcrumbs] = useState(BREADCRUMBS[type])
  const [loading, setLoading] = useState<boolean>(true)
  const { id } = useParams()
  const { pathname } = useLocation()
  const { portal } = usePortal()
  const isTablet = useMediaQuery({
    query: '(max-width: 1023px)'
  })

  const { response, fire, error } = useAxios()

  const getDetailCard = useCallback(() => {
    switch (type) {
      case 'community':
        return (
          <CommunityDetailCard
            loading={loading}
            {...(response?.data as ArticleData)}
          />
        )
      case 'event':
        return (
          <EventDetailCard
            loading={loading}
            {...(response?.data as ArticleData)}
          />
        )
    }
  }, [loading])

  useEffect(() => {
    fire(
      getArticle(
        `/${type === 'community' ? 'communities' : 'events'}/item`,
        id ?? ''
      )
    ).then(({ response, error }) => {
      if (!error) {
        const newBreadCrumbs = [
          ...BREADCRUMBS[type],
          { label: response?.data?.title, path: pathname }
        ]
        setBreadcrumbs(newBreadCrumbs)
      }

      setLoading(false)
    })
  }, [])
  return (
    <div className={'page'}>
      {error?.response?.status === 404 ? (
        <NotFound></NotFound>
      ) : error ? (
        <div className={'page__content'}>{error.message}</div>
      ) : (
        <>
          {loading && isTablet ? (
            <div className="breadcrumbs">
              <Skeleton width="10rem" />
              <Skeleton width="10rem" />
              <Skeleton width="10rem" />
            </div>
          ) : (
            <Breadcrumbs
              className={'page__breadcrumbs'}
              breadcrumbs={breadcrumbs}
            />
          )}
          {portal &&
            createPortal(
              loading ? (
                <div className="breadcrumbs">
                  <Skeleton width="10rem" />
                  <Skeleton width="15rem" />
                  <Skeleton width="24rem" />
                </div>
              ) : (
                <Breadcrumbs breadcrumbs={breadcrumbs} />
              ),
              portal
            )}

          <div className="page__content article-page">
            {(type === 'event' && response?.data?.moderationStatusId === EventModerationStatusEnum.Error.id && response?.data?.moderationStatusMessage) && (
              <PageNotification
                text={response?.data?.moderationStatusMessage}
                className={'article-page__note'}
              >
                <Btn
                  to={`/events/${response?.data?.id}/edit`}
                >
                  Редактировать
                </Btn>
              </PageNotification>
            )}
            <div className="article-page__row">{getDetailCard()}</div>
            <div className="article-page__row">
              <div className="article-page__col">
                {loading ? (
                  <>
                    <Skeleton className="mb-6" height="7rem" />
                    <Skeleton count={10} />
                    <Skeleton className="my-6" height="7rem" />
                    <Skeleton count={3} />
                    <Skeleton className="my-4" height="30rem" />
                    <Skeleton count={5} />
                  </>
                ) : (
                  <>
                    <TextContent
                      content={response?.data?.detailText}
                    ></TextContent>
                  </>
                )}
                {loading ? null : (
                  <>
                    {type === 'event' ? (
                      <EventRating
                        informationItem={response?.data?.informationItems[0]}
                        title={response?.data?.title}
                        id={response?.data?.id}
                      ></EventRating>
                    ) : null}
                  </>
                )}
              </div>
              <div className="article-page__col">
                {loading ? (
                  <Card>
                    <div className="contact-card">
                      <Skeleton height="70px" />
                      <Skeleton count={2} />
                      <Skeleton count={2} />
                      <Skeleton count={2} />
                    </div>
                  </Card>
                ) : (
                  response?.data?.vendor && (
                    <ContactCard {...response?.data.vendor} />
                  )
                )}

                {loading ? (
                  <Card>
                    <div className="contact-card">
                      <Skeleton height="70px" />
                      <Skeleton count={2} />
                      <Skeleton count={2} />
                    </div>
                  </Card>
                ) : (
                  response?.data?.chief && (
                    <ContactCard {...response.data.chief} />
                  )
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Article
